import { Briefcase, GraduationCap, Coins, BookOpen, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

const categories = [
  {
    id: 'emploi',
    title: 'Emplois',
    description: 'Offres d\'emploi CDI, CDD',
    icon: Briefcase,
    color: 'bg-primary',
    count: '150+'
  },
  {
    id: 'stage',
    title: 'Stages',
    description: 'Stages rémunérés et non rémunérés',
    icon: GraduationCap,
    color: 'bg-secondary',
    count: '80+'
  },
  {
    id: 'bourse',
    title: 'Bourses',
    description: 'Bourses d\'études locales et internationales',
    icon: Coins,
    color: 'bg-success',
    count: '45+'
  },
  {
    id: 'formation',
    title: 'Formations',
    description: 'Formations professionnelles certifiantes',
    icon: BookOpen,
    color: 'bg-accent',
    count: '120+'
  },
  {
    id: 'volontariat',
    title: 'Volontariat',
    description: 'Missions de volontariat et bénévolat',
    icon: Heart,
    color: 'bg-destructive',
    count: '30+'
  }
];

export function OpportunityCategories() {
  const sectionRef = useScrollAnimation();
  
  return (
    <section ref={sectionRef} className="relative section-padding bg-background overflow-hidden scroll-reveal">
      {/* Refined Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-60">
        <div className="absolute top-20 left-10 w-[600px] h-[600px] bg-primary/6 rounded-full blur-[140px] animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-[700px] h-[700px] bg-secondary/6 rounded-full blur-[160px] animate-pulse animation-delay-300"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="text-center mb-24 animate-fade-in-up max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground mb-8 tracking-tight leading-tight">
            Explorez les Opportunités
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground font-light leading-relaxed">
            Découvrez toutes les catégories d'opportunités disponibles pour booster votre carrière
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
          {categories.map((category, index) => {
            const Icon = category.icon;
            return (
              <Link
                key={category.id}
                to={`/opportunities?type=${category.id}`}
                className="group relative glass rounded-[2rem] p-10 border border-border/50 hover:border-primary/60 transition-all duration-500 hover:-translate-y-6 hover:rotate-1 hover-glow animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="relative mb-10">
                    <div className="relative w-28 h-28 rounded-[1.5rem] bg-gradient-to-br from-primary/10 via-primary/5 to-transparent flex items-center justify-center mb-5 group-hover:scale-125 group-hover:rotate-6 transition-all duration-500 shadow-md">
                      <div className="absolute inset-0 rounded-[1.5rem] bg-primary opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                      <Icon className="relative h-14 w-14 text-primary group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500" strokeWidth={2.5} />
                    </div>
                    <span className="absolute -top-4 -right-4 bg-gradient-to-r from-primary to-primary-dark text-primary-foreground px-5 py-2.5 rounded-2xl text-sm font-bold shadow-xl group-hover:scale-110 transition-transform">
                      {category.count}
                    </span>
                  </div>
                  <h3 className="font-bold text-2xl text-foreground mb-4 group-hover:text-primary transition-colors duration-300 tracking-tight">
                    {category.title}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed font-light">
                    {category.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}